import React, { useMemo } from 'react';
import { Grid } from '@material-ui/core';
import { useSelector } from 'react-redux';
import { SUMMARY_TEETH } from './utils';
import { TopTeeth, bottomLeftTeeth, bottomRightTeeth } from '../../PrescriptionNote/LookUpType/teethArchType';
import { ReadOnlyTeeth } from './readOnlyTeeth';
import { CaseImpressionDTO, CaseImpressionFileType } from '../../../../pages/DiffRetainer/modal/type';
import { useLang } from '../../../../util/lang';
import ReadOnlyRetainerRequestHelper from './ReadOnlyRetainerRequestHelper';

/**
 * A functional component that renders a Grid item with a paragraph.
 *
 * @param {Object} props - The properties passed to the component.
 * @param {string} field - The field name.
 * @param {string} value - The value to display.
 * @param {Object} prescription - The form name from store.
 * @param {'upper' | 'lower'} impressionType
 */
const TextGrid = ({ value, field, prescription, impressionType }) => {
    // supportedRetainerTypes is from backend
    // and being stored to redux store when the page is loaded
    const differentRatiner = useSelector((state) => state.diffRetainer);
    const { supportedRetainerTypes } = differentRatiner;
    const getDiffRetainerLang = useLang('DiffRetainer');

    const readOnlyRetainerHelper = useMemo(() => {
        return new ReadOnlyRetainerRequestHelper(getDiffRetainerLang, supportedRetainerTypes);
    }, [getDiffRetainerLang, supportedRetainerTypes]);

    const displayValue = useMemo(() => {
        return readOnlyRetainerHelper.getDisplayValue(field, value, prescription, impressionType);
    }, [value, field, prescription, supportedRetainerTypes, impressionType]);
    // console.log("displayValue => ", displayValue)

    const textColor = displayValue === '-' ? 'lightgray' : 'black';
    return (
        <Grid item>
            <p style={{ color: textColor }}>{displayValue}</p>
        </Grid>
    );
};

/**
 * A functional component that renders a Grid item with either ReadOnlyTeeth components or a paragraph, based on the validity of the value prop.
 *
 * @param {Object} props - The properties passed to the component.
 * @param {number[]} [props.value=[]] - An array of selected teeth, represented by their numbers.
 * @param {string} props.field - The field name.
 * @param {boolean} props.isInSummary - The boolean to indicate if the component is in NewPatientSummary.
 * @example
 * <TeethGrid value={[1, 2, 3]} field="fieldName" />
 */
const TeethGrid = ({ value = [], field, isInSummary = false }) => {
    const isValid = Array.isArray(value) && value.length > 0;
    if (isInSummary) {
        return isValid ? (
            <ReadOnlyTeeth isInSummary={isInSummary} selectedTooth={value} teeth={SUMMARY_TEETH} />
        ) : (
            <p style={{ color: 'lightgray' }}>-</p>
        );
    }

    return (
        <Grid item>
            {isValid ? (
                <>
                    <ReadOnlyTeeth teeth={TopTeeth} side="Top" selectedTooth={value} />
                    <ReadOnlyTeeth
                        selectedTooth={value}
                        teeth={{
                            bottomRight: {
                                ...bottomRightTeeth,
                                minY: 45,
                                maxY: 120,
                            },
                            bottomLeft: {
                                ...bottomLeftTeeth,
                                minY: 45,
                                maxY: 120,
                            },
                        }}
                    />
                </>
            ) : (
                <p style={{ color: 'lightgray' }}>-</p>
            )}
        </Grid>
    );
};

/**
 * A functional component that renders a Grid item with a paragraph.
 *
 * @param {Object} props - The properties passed to the component.
 * @param {boolean} [props.value=false] - A value indicating whether a certain condition is met.
 * @param {CaseImpressionDTO} props.caseImpression - The case impression data.
 */
const FileUploadGrid = ({ caseImpression }) => {
    const getDiffRetainerLang = useLang('DiffRetainer');
    if (caseImpression.type === CaseImpressionFileType.LATEST_APPROVED_TREATMENT_PLAN) {
        return (
            <Grid item>
                <p>{getDiffRetainerLang('readOnlyPrescription.lastStepOfLatestApprovedTreatmentPlan')}</p>
                <p>{getDiffRetainerLang('readOnlyPrescription.referToDocument')}</p>
            </Grid>
        );
    }

    if (caseImpression.type === CaseImpressionFileType.LATEST_RETAINER_REQUEST) {
        return (
            <Grid item>
                <p>{getDiffRetainerLang('readOnlyPrescription.useLastRetainerRequestImpressions')}</p>
                <p>{getDiffRetainerLang('readOnlyPrescription.referToDocument')}</p>
            </Grid>
        );
    }

    if (caseImpression.type === CaseImpressionFileType.COURIER) {
        return (
            <Grid item>
                <p>{getDiffRetainerLang('readOnlyPrescription.physicalImpressionByCourier')}</p>
                <p>
                    {getDiffRetainerLang('readOnlyPrescription.waybillNumber')} : {caseImpression.waybill_number || ''}
                </p>
                <p>
                    {getDiffRetainerLang('readOnlyPrescription.courierCompany')} :{' '}
                    {caseImpression.courier_company || '-'}
                </p>
            </Grid>
        );
    }

    if (caseImpression.type === CaseImpressionFileType.SCANNER) {
        return (
            <Grid item>
                <p>{getDiffRetainerLang('readOnlyPrescription.digitalImpressionScannerPortal')}</p>
                <p>
                    {getDiffRetainerLang('readOnlyPrescription.scannerBrand')} : {caseImpression.scanner_brand || ''}
                </p>
                <p>
                    {getDiffRetainerLang('readOnlyPrescription.doctorName')} : {caseImpression.doctor_id || '-'}
                </p>
            </Grid>
        );
    }

    if (caseImpression.type === CaseImpressionFileType.FILE) {
        return (
            <Grid item>
                <p>{getDiffRetainerLang('readOnlyPrescription.referToViewImpression')}</p>
            </Grid>
        );
    }

    return (
        <Grid item>
            <p>-</p>
        </Grid>
    );
};

const AnswerGrid = ({ field, value, option = null, isInSummary = false, prescription, impressionType }) => {
    // handle file upload answer display
    if (field === 'impressionOption') {
        if (!value) return null;
        return <FileUploadGrid caseImpression={value} />;
    }

    // handle teeth slections answer display
    if (['BiteRamp', 'Pontics'].includes(field)) {
        return <TeethGrid value={value} field={field} isInSummary={isInSummary} />;
    }

    // handle general text display
    return <TextGrid value={value} field={field} prescription={prescription} impressionType={impressionType} />;
};

export default AnswerGrid;
